import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import {
  APIEntityType,
  ColorRangeFormatting,
  ColorRangePoint,
} from '@ardoq/api-types';
import type { FormattingContextState } from './types';
import { GetEntityFieldValue } from '../../types';

/** Calculates default min and max values for 'number' mode based on dataset values */
export const getDefaultColorRangeNumberValues = (
  rule: ComponentOrReferenceFormattingRule,
  normalizedValue: ColorRangeFormatting,
  datasetData: FormattingContextState['datasetData'],
  getEntityFieldValue?: GetEntityFieldValue
): { min: ColorRangePoint; max: ColorRangePoint } => {
  const entityIds =
    rule.type === APIEntityType.COMPONENT
      ? datasetData?.componentIds
      : datasetData?.referenceIds;

  if (
    !getEntityFieldValue ||
    !entityIds ||
    entityIds.length === 0 ||
    !rule.field
  ) {
    return {
      min: { ...normalizedValue.min, value: null },
      max: { ...normalizedValue.max, value: null },
    };
  }

  const uniqueValues = new Set<number>();

  for (const id of entityIds) {
    const value = getEntityFieldValue(rule.type, id, rule.field);

    if (value === null || value === undefined) {
      continue;
    }

    const numValue = Number(value);

    if (!Number.isFinite(numValue)) {
      continue;
    }

    // Add only unique numeric values
    uniqueValues.add(numValue);
  }

  // if there are less than 2 unique numeric values, return nulls
  if (uniqueValues.size < 2) {
    return {
      min: { ...normalizedValue.min, value: null },
      max: { ...normalizedValue.max, value: null },
    };
  }

  // Calculate min and max from unique numeric values
  const numbers = Array.from(uniqueValues);
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);

  return {
    min: { ...normalizedValue.min, value: min },
    max: { ...normalizedValue.max, value: max },
  };
};
